import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TablePagination,
  TableCell,
  tableCellClasses,
  Pagination,
  InputLabel,
  MenuItem,
  Select,
  FormControl,
  SelectChangeEvent,
  Checkbox,
  FormControlLabel,
  FormGroup,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Column } from "../../interfaces/table.interface";
import { startLoading, stopLoading } from "../../redux/slices/WindowSlice";
import { RootState } from "../../redux/store";
import { get } from "../../redux/ApiHelper";
import { CoordinateTierCategoryInterface } from "../../interfaces/pincode-tier.interface";
import { formatDateTime } from "../../utils/date&time.util";
import { MdAdd, MdEdit } from "react-icons/md";
import AddOrEditTierSetupModal from "./AddOrEditTierSetupModal";

const GeoCoordianteTierSetupSettings: React.FC = () => {
  const dispatch = useDispatch();
  const { signedAdmin } = useSelector((state: RootState) => state.admin);
  const [totalPages, setTotalPages] = useState(1);

  const [pinDocs, setPinDocs] = useState<CoordinateTierCategoryInterface[]>([]);

  // Handle tab change

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState("20");
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editData, setEditData] = useState<CoordinateTierCategoryInterface>();
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [submittedCount, setSubmittedCount] = useState<number>(0);
  const [isSortByName, setIsSortByName] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);

  const handleChangePage = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  const handleChangeRowsPerPage = (event: SelectChangeEvent) => {
    setRowsPerPage(event.target.value as string);
  };

  const handleCheck = () => {
    setIsSortByName(!isSortByName);
  };

  // console.log(rowsPerPage);
  // Fetch classes from API
  const fetchPinTiers = () => {
    dispatch(startLoading());
    const queryParams = new URLSearchParams();
    queryParams.set("page", String(page));
    queryParams.set("limit", String(rowsPerPage));
    queryParams.set("sortByName", isSortByName ? "yes" : "no");
    queryParams.set("type", String(signedAdmin.type));
    queryParams.set("adminId", String(signedAdmin.id));
    queryParams.set("searchTerm", searchQuery);
    const apiUrl = `/admin/pin-tier/get?${queryParams.toString()}`;

    get(apiUrl)
      .then((response) => {
        // setPinDocs((pinDocs) => ({
        //   ...pinDocs,
        //   ...response.docs,
        // }));
        setPinDocs(response?.docs);
        setPage(response?.page);
        setTotalPages(response?.totalPages);

        // setAllTuitionClassesNumber(response.totalResults);
        // console.log(response);
      })
      .catch((error) => console.error(`Error fetching data:`, error))
      .finally(() => dispatch(stopLoading()));
  };

  // useEffect(() => {
  //   fetchPinTiers();
  // }, [submittedCount, rowsPerPage, isSortByName, page, searchQuery]);

  // debounce effect
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500); // wait 500ms after user stops typing

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  // Fetch data when debouncedSearch changes instead of searchQuery
  useEffect(() => {
    fetchPinTiers();
  }, [submittedCount, rowsPerPage, isSortByName, page, debouncedSearch]);
  // Function to open modal
  const openModal = () => {
    setIsModalOpen(true);
    setEditData({
      center: { type: "Point", coordinates: [null, null] },
      radius: null,
      tierType: "",
      name: "",
    });
  };

  //   Function to close modal
  const closeModal = () => {
    setIsModalOpen(false);
    setEditData(null);
    setIsEdit(false);
  };

  const columns: Column[] = [
    {
      header: "co-ordinate",
      render: (_value: any, row: CoordinateTierCategoryInterface) =>
        `lat: ${row?.center.coordinates[1]}, lon: ${row?.center.coordinates[0]}` ||
        "N/A",
    },
    {
      header: "Area name",
      render: (_value: any, row: CoordinateTierCategoryInterface) =>
        row?.name || "N/A",
    },
    {
      header: "radius (km)",
      render: (_value: any, row: CoordinateTierCategoryInterface) =>
        row?.radius || "N/A",
    },
    {
      header: "Tier type",
      render: (_value: any, row: CoordinateTierCategoryInterface) =>
        row?.tierType || "N/A",
    },

    {
      header: "Last Modified",
      render: (_value: any, row: CoordinateTierCategoryInterface) =>
        formatDateTime(row?.updatedAt) || "N/A",
    },
    {
      header: "Action",
      render: (_value: any, row: CoordinateTierCategoryInterface) => {
        return (
          <div
            className="rounded-full bg-slate-200 p-2 text-yellow-500 cursor-pointer flex w-[30px]"
            onClick={() => {
              setEditData(row);
              setIsEdit(true);
              setIsModalOpen(true);
            }}
          >
            <MdEdit className="m-auto" />
          </div>
        );
      },
    },

    // { select: "meetingLink", header: "Meeting link" },
  ];

  return (
    <div className="coupon table-container max-h-[100vh] flex flex-col">
      <div className="title">co-ordinate and tier type setup</div>
      <div
        onClick={() => {
          openModal();
          setIsEdit(false);
        }}
        className="fixed top-8 right-8 bg-yellow-500 text-white p-2 rounded-full cursor-pointer shadow-lg hover:bg-yellow-600"
      >
        <MdAdd />
      </div>
      <div className="search-container mb-4 flex space-x-4">
        <input
          type="text"
          className="w-[50%] px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-700"
          placeholder="Search..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <FormControl
          sx={{
            "& .MuiInputLabel-root": { color: "yellow" }, // label color
            "& .MuiOutlinedInput-root": {
              "& fieldset": { borderColor: "yellow" }, // default border
              "&:hover fieldset": { borderColor: "gold" }, // on hover
              "&.Mui-focused fieldset": { borderColor: "yellow" }, // when focused
            },
            "& .MuiSelect-select": { color: "yellow" }, // text color
            "& .MuiSvgIcon-root": { color: "yellow" }, // dropdown arrow color
          }}
        >
          <InputLabel>Rows per page</InputLabel>
          <Select
            labelId="demo-simple-select-label"
            label="Rows per page"
            className="w-[150px]"
            value={rowsPerPage}
            onChange={handleChangeRowsPerPage}
          >
            <MenuItem value={"5"}>Five</MenuItem>
            <MenuItem value={"20"}>Twenty</MenuItem>
            <MenuItem value={"30"}>Thirty</MenuItem>
            <MenuItem value={"50"}>Fifty</MenuItem>
          </Select>
        </FormControl>
        <FormGroup>
          <FormControlLabel
            required
            control={
              <Checkbox
                checked={isSortByName}
                onChange={handleCheck}
                sx={{
                  color: "yellow", // default color
                  "&.Mui-checked": { color: "yellow" }, // when checked
                }}
              />
            }
            label="Sort by area name"
            sx={{
              color: "yellow", // label color
            }}
          />
        </FormGroup>
      </div>
      <TableContainer>
        <div className="overflow-x-auto max-w-[100vw]">
          <div className="table-component-container">
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 700 }} aria-label="customized table">
                <TableHead>
                  <TableRow>
                    {columns?.map((column, index) => (
                      <StyledTableCell
                        key={index}
                        style={{
                          textAlign: column?.align || "center",
                          padding: "4px 16px",
                        }}
                      >
                        {column.header}
                      </StyledTableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody className="">
                  {pinDocs?.length === 0 ? (
                    <StyledTableRow>
                      <StyledTableCell
                        colSpan={columns?.length}
                        style={{ textAlign: "center" }}
                      >
                        No Data Available
                      </StyledTableCell>
                    </StyledTableRow>
                  ) : (
                    pinDocs?.map((row, rowIndex) => (
                      <StyledTableRow key={rowIndex}>
                        {columns?.map((column, colIndex) => (
                          <StyledTableCell
                            key={colIndex}
                            style={{
                              textAlign: column?.align || "center",
                              padding: "8px 16px",
                            }}
                          >
                            {column?.render
                              ? column?.render(row[column.select], row)
                              : row[column.select]}
                          </StyledTableCell>
                        ))}
                      </StyledTableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </div>
      </TableContainer>
      <div className="my-2 flex justify-center">
        <Pagination
          count={totalPages}
          page={page}
          onChange={handleChangePage}
          sx={{
            "& .MuiPaginationItem-root": {
              color: "#FFF", // Other pages
            },
            "& .Mui-selected": {
              color: "yellow", // Selected page
              backgroundColor: "#FFC727", // Optional
            },
          }}
        />
      </div>
      {/* Create Coupon Modal */}
      <AddOrEditTierSetupModal
        isOpen={isModalOpen}
        closeModal={closeModal}
        isEdit={isEdit}
        editData={editData}
        setSubmittedCount={setSubmittedCount}
      />
    </div>
  );
};

export default GeoCoordianteTierSetupSettings;

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: "var(--color-primary)",
    padding: 4,
    textAlign: "center",
    position: "sticky",
    top: 0,
    zIndex: 1, // Ensure it is on top of the body
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    padding: 4,
    textAlign: "center",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));
